import { Component, ErrorInfo, ReactNode } from 'react';
import toast from 'react-hot-toast';
import { shouldIgnoreError } from '../utils/globalErrorHandler';
import { sendDebugLog } from '../utils/debugLogging';
import '../styles/globals.css';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // IMMEDIATE console.log - executes synchronously
    console.error('[DEBUG ERROR_BOUNDARY] ErrorBoundary caught error', {
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack?.substring(0, 500),
      componentStack: errorInfo.componentStack?.substring(0, 500),
      hypothesisId: 'ERROR_BOUNDARY'
    });
    
    // #region agent log
    // Defer logging to avoid React error #300
    setTimeout(() => {
      sendDebugLog({
        location: 'ErrorBoundary.tsx:31',
        message: 'ErrorBoundary caught error',
        data: {
          errorMessage: error.message,
          errorName: error.name,
          errorStack: error.stack?.substring(0, 200),
          componentStack: errorInfo.componentStack?.substring(0, 200),
          hypothesisId: 'A'
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'run1'
      });
    }, 0);
    // #endregion
    
    // Check if this error should be ignored
    if (shouldIgnoreError(error)) {
      return;
    }

    // Extract user-friendly error message
    const errorMessage = this.extractErrorMessage(error);

    // Show toast notification
    toast.error(errorMessage, {
      duration: 5000,
      position: 'top-right',
      style: {
        background: '#dc2626',
        color: '#fff',
        fontWeight: '500',
      },
    });

    // Log full error details only when logging is enabled
    // Import logger dynamically to avoid circular dependency
    import('../utils/logger').then(({ logger }) => {
      if (logger.isEnabled()) {
        logger.error('ErrorBoundary caught an error:', error, errorInfo);
      }
    }).catch(() => {
      // Silently fail if logger is not available
    });
  }

  private extractErrorMessage(error: Error): string {
    // Try to extract meaningful message
    if (error.message) {
      // Hide stack traces and technical details
      const message = error.message.split('\n')[0];
      return message.length > 100 ? `${message.substring(0, 100)}...` : message;
    }

    // Fallback for errors without messages
    if (error.name) {
      return `${error.name}: An unexpected error occurred`;
    }

    return 'An unexpected error occurred. Please try again.';
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary-container">
          <h2 className="error-boundary-title">
            Something went wrong
          </h2>
          <p className="error-boundary-message">
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <button
            onClick={this.handleReset}
            className="error-boundary-button"
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

